import Conviva from '@convivainc/conviva-js-coresdk';
import { ClientType, ColorSpace } from '@mlbtv-clients/services';
import { WIRELESS } from 'analytics/conviva/constants';
import { releaseConvivaAnalyticsSingleton } from 'analytics/conviva/hooks/useConviva/convivaAnalyticsSingleton';
import { EASTERN_TZ } from 'constants/date';
import { MODEL_YEAR_UNKNOWN } from 'constants/platforms';
import { ROUTES } from 'constants/screens';
import { ARROW_KEYS } from 'constants/spatialNavigation';
import { US_COUNTRY_CODE } from 'constants/userInfo';
import { ModelYearsConfig } from 'core-app/store/config';
import { ModelTier } from 'hooks/useDeviceModelTier/types';
import { getModelTier } from 'hooks/useDeviceModelTier/utils';
import { NavigateFunction } from 'react-router-dom';
import { EPGErrors } from 'services/epg/constants';
import fallback from 'store/config/fallback.json';

const { EPG_SERVICE_ERROR } = EPGErrors;

interface DeviceMetadata {
  deviceBrand: string;
  deviceManufacturer: string;
  deviceModel: string;
  operatingSystemName: string;
}

const {
  Constants: { DeviceCategory, DeviceMetadata, DeviceType },
} = Conviva;

const {
  BRAND,
  CATEGORY,
  MANUFACTURER,
  MODEL,
  OS_NAME,
  SCREEN_RESOLUTION_HEIGHT,
  SCREEN_RESOLUTION_WIDTH,
  TYPE,
} = DeviceMetadata;

// Disable window scrolling with arrow keys
window.addEventListener('keydown', (e) => {
  if (ARROW_KEYS.has(e.keyCode)) {
    e.preventDefault();
  }
});

export const getClientType = async () => ClientType.WEB;

export const getDeviceOSVersion = async (defaultOS: string) => defaultOS;

export const getDeviceAdParams = (adParams: Record<string, string>, ifaParams: unknown) => adParams;

export const getDeviceCaptionStyle = async () => {
  return `
    ::cue {
      color: cyan;
    }
  `;
};

export const getDeviceCountryCode = async () => US_COUNTRY_CODE;

export const getDeviceConnectionType = async () => WIRELESS;

export const getDeviceExitApp = () => {
  releaseConvivaAnalyticsSingleton();
  window.location.href = 'https://www.mlb.com/';
};

export const getDeviceExitConfirmation = (navigate?: NavigateFunction) => {
  if (navigate) navigate(ROUTES.EXIT);
};

export const getDeviceId = async (deviceId: string) => deviceId;

export const getDeviceIdType = () => 'dpid';

// TODO: does playstation have this information available
export const getDeviceKeyboardIsOpen = async () => false;

export const getDeviceMetadata = async (deviceMetadata: DeviceMetadata) => {
  const { deviceBrand, deviceManufacturer, deviceModel, operatingSystemName } = deviceMetadata;

  return {
    [BRAND]: deviceBrand,
    [CATEGORY]: DeviceCategory.DESKTOP_APP,
    [MANUFACTURER]: deviceManufacturer, //we will use the default for each platform to control analytics reporting as some devices have unreliable values for this field (ex: comcast smart tvs returning 'xglobal')
    [MODEL]: deviceModel,
    [OS_NAME]: operatingSystemName,
    [SCREEN_RESOLUTION_HEIGHT]: window.screen.height,
    [SCREEN_RESOLUTION_WIDTH]: window.screen.width,
    [TYPE]: DeviceType.DESKTOP,
  };
};

export const getDeviceModelTier = async (modelTiers: Record<string, ModelTier>) =>
  getModelTier(modelTiers, '');

export const getDeviceModelYear = async (_modelYearsConfig: ModelYearsConfig) => MODEL_YEAR_UNKNOWN;

export const getDeviceHasSpinner = async (
  hasSpinner: typeof fallback.featureFlags.animations.hasSpinner,
) => !hasSpinner.hasAllModelsDisabled;

export const getDevicePlaybackCapabilities = async () => ({ colorSpace: [ColorSpace.HDR] });

export const getDeviceModel = async (defaultValue: string) => defaultValue;

export const getDeviceSKU = async (defaultValue: string) => defaultValue;

export const getDeviceTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!timeZone) {
    throw new Error(`${EPG_SERVICE_ERROR} Time zone not found. Falling back to ${EASTERN_TZ}`);
  }

  return timeZone;
};

export const getDeviceTTSEnabled = () => false;
